import React from "react";
import {
  Sparkles,
  ShieldCheck,
  Clock,
  Award,
  Users,
  Search,
} from "lucide-react";
import { motion } from "framer-motion";

const ServicesHero = ({ searchQuery, setSearchQuery, selectedCategory, setSelectedCategory, categories }) => {
  const stats = [
    { value: "10,000+", label: "Properties Handled", icon: Users },
    { value: "100%", label: "Verified & Compliant", icon: ShieldCheck },
    { value: "24-48h", label: "Fast Turnaround", icon: Clock },
    { value: "4.9/5", label: "Client Satisfaction", icon: Award },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-gray-900 via-[#121212] to-gray-950 text-white pt-24 pb-20 px-4 sm:px-6 lg:px-12 -mt-20">
      {/* Ambient background glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-rose-700/15 rounded-full blur-3xl pointer-events-none" />
      
      {/* Geometric background mesh */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto text-center">
        {/* Top badge */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs sm:text-sm font-semibold text-red-300 tracking-wide uppercase mb-6"
        >
          <Sparkles className="w-4 h-4 text-red-400" />
          <span>Complete Real Estate Concierge</span>
        </motion.div>

        {/* Main Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.1] max-w-5xl mx-auto"
        >
          End-to-End Property Care,{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-rose-400 to-amber-300">
            Simplified & Delivered.
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-6 text-base sm:text-xl text-gray-300 max-w-3xl mx-auto font-normal leading-relaxed"
        >
          From tenant management and legal e-Khata verification to custom wall painting, 
          instant rental agreements, and designer furniture rentals — HousyHunt powers every step 
          of your property lifecycle under one roof.
        </motion.p>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-10 max-w-2xl mx-auto relative"
        >
          <div className="relative flex items-center bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl p-2 shadow-2xl focus-within:border-red-500/80 transition-all">
            <Search className="w-5 h-5 text-gray-400 ml-4 shrink-0" />
            <input
              type="text"
              placeholder="Search services (e.g., e-Khata, Painting, Agreement, Management)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent px-4 py-3 text-sm sm:text-base text-white placeholder-gray-400 focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white mr-2 bg-white/10 rounded-lg transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </motion.div>

        {/* Category Filter Pills */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.35 }}
          className="mt-8 flex flex-wrap justify-center gap-2 sm:gap-3"
        >
          {categories.map((cat) => {
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 sm:px-5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer ${
                  isActive
                    ? "bg-red-600 text-white shadow-lg shadow-red-600/40 scale-105"
                    : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                {cat.name}
              </button>
            );
          })}
        </motion.div>

        {/* Stats Strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto"
        >
          {stats.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={idx}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-5 flex items-center gap-4 text-left hover:border-red-500/40 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-red-600/15 border border-red-500/30 flex items-center justify-center shrink-0 text-red-400">
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-black text-white leading-tight">
                    {item.value}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-400 font-medium">
                    {item.label}
                  </p>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesHero;
